<script setup>
import { computed, ref, useTemplateRef, nextTick } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 2000;

const retryCount = ref(0);
const videoElement = useTemplateRef('videoElement');

const videoURL = computed(() => {
  if (retryCount.value < 0) return '';
  return timeStampAppendedURL(attachment.dataUrl);
});

const onSourceError = () => {
  if (retryCount.value < MAX_RETRIES) {
    const delay = INITIAL_RETRY_DELAY * 2 ** retryCount.value;
    setTimeout(() => {
      retryCount.value += 1;
      nextTick(() => {
        videoElement.value?.load();
      });
    }, delay);
  }
};

const showGallery = ref(false);

const { filteredCurrentChatAttachments } = useMessageContext();
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer relative group"
    @click="showGallery = true"
  >
    <video
      ref="videoElement"
      class="w-full h-full object-cover"
      muted
      playsInline
    >
      <source :src="videoURL" @error="onSourceError" />
    </video>
    <div
      class="absolute w-full h-full inset-0 p-1 flex items-center justify-center"
    >
      <div
        class="size-7 bg-n-slate-1/60 backdrop-blur-sm rounded-full overflow-hidden shadow-[0_5px_15px_rgba(0,0,0,0.4)]"
      >
        <Icon
          icon="i-teenyicons-play-small-solid"
          class="size-7 text-n-slate-12/80 backdrop-blur"
        />
      </div>
    </div>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onError"
    @close="() => (showGallery = false)"
  />
</template>
